import { $getSelection, $isParagraphNode, type BaseSelection } from 'lexical';
import { WrapTextIcon } from 'lucide-react';
import { useCallback, useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { useToolbarContext } from '../../context/toolbar-context';
import { useUpdateToolbarHandler } from '../../editor-hooks/use-update-toolbar';
import { $isLineHeightNode, LineHeightNode } from '../../nodes/line-height-node';

const LINE_HEIGHT_OPTIONS = [
  { value: '1', label: '1.0' },
  { value: '1.15', label: '1.15' },
  { value: '1.5', label: '1.5' },
  { value: '2', label: '2.0' },
  { value: '2.5', label: '2.5' },
  { value: '3', label: '3.0' },
];

export function LineHeightToolbarPlugin() {
  const [lineHeight, setLineHeight] = useState('1.5');
  const { activeEditor } = useToolbarContext();

  const $updateToolbar = useCallback((selection: BaseSelection) => {
    // Try to detect current line height from selected nodes
    const nodes = selection.getNodes();
    for (const node of nodes) {
      // Get the parent paragraph node
      let paragraphNode: any = node;
      if (!$isParagraphNode(node) && !$isLineHeightNode(node)) {
        const parent = node.getParent();
        if (parent) {
          paragraphNode = parent;
        }
      }

      if ($isLineHeightNode(paragraphNode)) {
        const style = paragraphNode.getStyle();
        if (style) {
          const match = style.match(/line-height:\s*([0-9.]+)/);
          if (match) {
            setLineHeight(match[1]);
            return;
          }
        }
      } else if ($isParagraphNode(paragraphNode)) {
        // For regular paragraphs without explicit style, default to "1.5" (matches our CSS)
        setLineHeight('1.5');
        return;
      }
    }
    setLineHeight('1.5'); // default
  }, []);

  useUpdateToolbarHandler($updateToolbar);

  const applyLineHeight = useCallback(
    (selectedLineHeight: string) => {
      activeEditor.update(() => {
        const selection = $getSelection();
        if (!selection) return;

        const selectedNodes = selection.getNodes();

        // Get all unique paragraph parents
        const paragraphsToUpdate = new Set();
        selectedNodes.forEach((node) => {
          let paragraphNode: any = node;
          if (!$isParagraphNode(node) && !$isLineHeightNode(node)) {
            const parent = node.getParent();
            if (parent) {
              paragraphNode = parent;
            }
          }

          if ($isParagraphNode(paragraphNode) || $isLineHeightNode(paragraphNode)) {
            paragraphsToUpdate.add(paragraphNode);
          }
        });

        // Apply line height to each paragraph
        paragraphsToUpdate.forEach((node: any) => {
          if ($isParagraphNode(node)) {
            let targetNode = node;

            // Convert to LineHeightNode if not already
            if (!$isLineHeightNode(node)) {
              const newNode = new LineHeightNode();
              newNode.append(...node.getChildren());
              node.replace(newNode);
              targetNode = newNode;
            }

            // Apply the line height style
            if ($isLineHeightNode(targetNode)) {
              targetNode.setStyle(`line-height:${selectedLineHeight}`);
            }
          } else if ($isLineHeightNode(node)) {
            // Already a LineHeightNode, just update style
            node.setStyle(`line-height:${selectedLineHeight}`);
          }
        });
      });
    },
    [activeEditor]
  );

  const handleLineHeightChange = useCallback(
    (value: string) => {
      setLineHeight(value);
      applyLineHeight(value);
    },
    [applyLineHeight]
  );

  return (
    <Select value={lineHeight} onValueChange={handleLineHeightChange}>
      <SelectTrigger className="!h-8 w-min gap-1">
        <WrapTextIcon className="size-4" />
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {LINE_HEIGHT_OPTIONS.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
